import { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Card, Tooltip, Typography } from 'antd';

import { Icon } from '@eduzz/ui-antd-theme';

import NoImage from '/no-image.png';

import { cn } from '@/lib/cn';
import { calcInstallment, formatBrlMoney } from '@/lib/functions';
import useProductStore from '@/stores/productStore';

const PreviewProduct = () => {
  const [showContent, setShowContent] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const product = useProductStore(d => d);
  const contentRef = useRef<HTMLDivElement>(null);

  const { price, installmentPrice } = useMemo(() => {
    const isWithoutInterest = product.upgradeSales.some(f => f.feature === 'installments_without_interest');

    const installmentValue = calcInstallment(product.payment.price!, 12, isWithoutInterest);

    return {
      price: formatBrlMoney(product.payment.price!),
      installmentPrice: formatBrlMoney(installmentValue).replace(/R\$\s/, '')
    };
  }, [product.payment.price, product.upgradeSales]);

  useEffect(() => {
    setShowContent(false);
  }, [product.description]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    setShouldShowToggle(el.scrollHeight > el.clientHeight);
  }, [product.description, showContent]);

  return (
    <Card
      className='border-outline-disabled rounded-default w-80 xl:w-113'
      classNames={{ body: 'flex flex-col divide-y divide-black/6 p-0' }}
    >
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center gap-2'>
          <div className='rounded-default flex h-24 w-24 shrink-0 grow-0 items-center justify-center divide-zinc-700'>
            <img src={product.image || NoImage} className='rounded-default w-full' />
          </div>
          <Tooltip title={product.title || 'Título do seu produto'}>
            <Typography.Title level={4} className='mb-0 line-clamp-2'>
              {product.title || 'Título do seu produto'}
            </Typography.Title>
          </Tooltip>
        </div>

        <div className='text-sm'>
          {product.payment.type === 'free' && <span className='text-base font-bold text-lime-700'>Gratuito</span>}
          {product.payment.type === 'open' && <span className='text-base font-bold text-lime-700'>Valor aberto</span>}
          {product.payment.type === 'oneTime' && (
            <div>
              em até <b>12x de R$</b>{' '}
              <span className='text-xl font-bold text-lime-700 dark:text-lime-500'>{installmentPrice}</span>
              <div>
                ou <b>{price}</b> à vista
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col p-4'>
        <div
          ref={contentRef}
          className={cn(
            'relative max-h-40 overflow-hidden transition-all duration-300 ease-in-out',
            {
              'after:to-surface-default after:absolute after:bottom-0 after:z-10 after:h-8 after:w-full after:bg-linear-to-b after:from-transparent':
                !showContent && shouldShowToggle
            },
            { 'max-h-none': showContent }
          )}
        >
          <div className='relative z-0'>
            <p className='mb-2 text-xs font-semibold'>Sobre o produto</p>
            {product.description && (
              <span className='text-content-subtitle text-sm whitespace-pre-wrap'>{product.description}</span>
            )}
            {!product.description && (
              <div className='flex w-full flex-col gap-1'>
                <span className='rounded-default block h-3.5 w-full bg-gray-100 dark:bg-neutral-700' />
                <span className='rounded-default block h-3.5 w-full bg-gray-100 dark:bg-neutral-700' />
                <span className='rounded-default block h-3.5 w-full bg-gray-100 dark:bg-neutral-700' />
              </div>
            )}
          </div>
        </div>

        {(shouldShowToggle || showContent) && (
          <Button
            id='preview-product-button'
            type='text'
            color='primary'
            size='small'
            className='mt-2 h-auto px-0'
            iconPosition='end'
            icon={
              <Icon.ChevronDown
                className={cn('duration-300', {
                  'rotate-180': showContent,
                  'rotate-0': !showContent
                })}
              />
            }
            onClick={() => setShowContent(!showContent)}
          >
            {!showContent ? 'Mostrar mais' : 'Mostrar menos'}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default PreviewProduct;
